import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { toast } from "../../components/toast";

import { useLocalStorage } from "../../hooks/use-local-storage";
import type { TQueue, QueueStatus } from "./types";
import { navLocale } from "../../lib/locale";

/**
 * 取込の進行を追うためのキー。取込完了時のキャッシュ破棄から除外する
 * (これらを消すとポーリング自身が壊れる / 段の判定が飛ぶ)。
 */
const INGEST_QUERY_KEYS = ["queue-def", "ingest-status"];

/**
 * 取込完了時にキャッシュを「古い」印にするか。取込機構自身のキー**以外は全て**対象。
 *
 * 取込が終わると版が変わるので、データ由来のキャッシュは軒並み古い。許可リスト方式にすると
 * 新しい画面のキーを足し忘れて「古いまま表示される」に戻るため、除外リスト方式にしている。
 */
export function shouldInvalidateOnIngestComplete(queryKey: readonly unknown[]): boolean {
  return !INGEST_QUERY_KEYS.includes(String(queryKey[0]));
}

export function useQueue() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [queueMap, setQueueMap] = useLocalStorage<Record<string, QueueStatus>>(
    "queue",
    {}
  );

  const [errorModal, setErrorModal] = React.useState<{
    isOpen: boolean;
    errorCode?: string;
    errorMessage?: string;
    fileName?: string;
  }>({
    isOpen: false,
  });

  React.useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe((listener) => {
      if (
        listener.type !== "updated" ||
        listener.query.queryKey[0] !== "queue-def"
      ) {
        return;
      }

      const newQueue = listener.query.state.data as TQueue[] | undefined;
      if (!newQueue) return;

      const nextQueueMap = { ...queueMap };

      newQueue.forEach((queue) => {
        const id = queue.queueId;

        if (
          !queue.steps ||
          !Array.isArray(queue.steps) ||
          typeof queue.currentStep !== "number" ||
          !queue.steps[queue.currentStep]
        ) {
          return;
        }

        const prevStatus = queueMap?.[id];
        const currStatus = queue.steps[queue.currentStep]?.status;

        if (!currStatus) return;

        if (currStatus === "loading") {
          nextQueueMap[id] = "loading";
          return;
        }

        if (prevStatus === "loading") {
          const itemName = queue.meta?.data?.name ?? t("unknown-item");
          if (currStatus === "success") {
            toast({
              type: "success",
              message: `${t(
                "data-mgmt-store-dialog-toast-success2"
              )}\n${itemName}`,
            });
            // 取込が完了した = データの版が変わったので、データ由来のキャッシュは全て古い。
            // ノード等の主要クエリは staleTime/gcTime=Infinity で、この直後の遷移で
            // **後から mount する** ページの useQueueReload は完了を観測できない
            // (mount 時点で既に完了済み) ため、ここで古い印を付けないと
            // 「タブを開くと古いノードのまま・リロードで直る」になる。
            //
            // ⚠ **ここで再取得はしない** (`refetchType: "none"`)。resetQueries や既定の
            // invalidate は在庫の全 active クエリを即座に叩き直すので、この時点ではまだ
            // 離散化されていない `/xcausal/v3/querybar` が 422 を返し、グローバルエラーが
            // 立って DialogProvider が ErrorDialog 側に切り替わる = **直後に出るはずの
            // tips ポップアップが表示されなくなる**。invalidate だけなら staleTime=Infinity を
            // 上書きして「古い」印が付き、各ページの mount 時 (refetchOnMount 既定 true) に
            // 取り直される。既に開いているページはページ側の useQueueReload が面倒を見る。
            queryClient.invalidateQueries({
              predicate: (query) => shouldInvalidateOnIngestComplete(query.queryKey),
              refetchType: "none",
            });
            navigate(navLocale("/data-manager/node?tips=node"));
          } else if (currStatus === "error") {
            // CSVエラーの場合はモーダルを表示
            const errorInfo = queue.steps[queue.currentStep]?.error;
            const metaErrorCode = queue.meta?.data?.error_code;
            
            setErrorModal({
              isOpen: true,
              errorCode: metaErrorCode || errorInfo?.code,
              errorMessage: metaErrorCode ? queue.meta?.data?.desc : errorInfo?.message,
              fileName: itemName,
            });
            toast({
              type: "error",
              message: `${t(
                "data-mgmt-store-dialog-toast-error2"
              )}\n${itemName}`,
            });
          }
        }
        nextQueueMap[id] = currStatus;
      });
      setQueueMap(nextQueueMap);
    });
    return () => unsubscribe();
  }, [navigate, queryClient, queueMap, setQueueMap, t]);

  const closeErrorModal = () => {
    setErrorModal({ isOpen: false });
  };

  return {
    errorModal: {
      isOpen: errorModal.isOpen,
      onClose: closeErrorModal,
      errorCode: errorModal.errorCode,
      errorMessage: errorModal.errorMessage,
      fileName: errorModal.fileName,
    },
  };
}
